import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, CalendarDays, CalendarRange, Send } from "lucide-react";

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));

export const ReportSchedule = () => {
  const [hour, setHour] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/report-schedule").then((r) => setHour(r.data.hour)); }, []);

  const save = async (h) => {
    setBusy(true);
    try {
      const r = await api.put("/settings/report-schedule", { hour: Number(h) });
      setHour(r.data.hour);
      toast.success(`Günlük rapor saati ${String(r.data.hour).padStart(2, "0")}:00 olarak kaydedildi`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const sendNow = async (path, label) => {
    setBusy(true);
    try {
      const r = await api.post(path);
      toast.success(`${label} ${r.data.to.join(", ")} adresine gönderildi`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="report-schedule">
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-base font-bold md:text-lg">Rapor Zamanlaması</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-secondary/40 p-4">
          <p className="text-sm font-semibold">Günlük Z Raporu + Stok Takibi</p>
          <p className="mb-3 text-xs text-muted-foreground">Her gün seçilen saatte (Türkiye saati) alıcılara gönderilir. Ayrıca her kullanıcıya kendi satış ve stok özeti profil e-postasına gider.</p>
          <Select value={hour === null ? undefined : String(hour)} onValueChange={save} disabled={busy || hour === null}>
            <SelectTrigger className="w-40" data-testid="report-hour-select"><SelectValue placeholder="Saat" /></SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => <SelectItem key={h} value={String(Number(h))} data-testid={`report-hour-${h}`}>{h}:00</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-lg bg-secondary/40 p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold"><CalendarDays className="h-4 w-4" /> Haftalık Özet</p>
          <p className="mb-3 text-xs text-muted-foreground">Her Pazartesi 09:00'da geçen hafta, önceki haftayla karşılaştırılarak gönderilir.</p>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => sendNow("/reports/weekly/email", "Haftalık özet")} data-testid="weekly-email-now">
            <Send className="mr-1.5 h-4 w-4" /> Şimdi gönder
          </Button>
        </div>
        <div className="rounded-lg bg-secondary/40 p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold"><CalendarRange className="h-4 w-4" /> Aylık Özet</p>
          <p className="mb-3 text-xs text-muted-foreground">Her ayın 1'inde 09:00'da geçen ayın geliri, kârı ve en çok satanları gönderilir.</p>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => sendNow("/reports/monthly/email", "Aylık özet")} data-testid="monthly-email-now">
            <Send className="mr-1.5 h-4 w-4" /> Şimdi gönder
          </Button>
        </div>
      </div>
    </section>
  );
};
